import '../../Styles/Partials/TextViewerModal.css'
import { useEffect, useRef, FC, MouseEvent, useState, useCallback, useMemo } from 'react'
import { CloseButton } from '../Layout/Buttons/CloseButton';
import { useAttachmentContext } from '../../contexts/AttachmentContext';
import CodeDisplay from './CodeDisplay';
import PDFViewer from '../Resources/PDFViewer';
import { cleanCodeTags } from '../../helpers/chatHelper';

export const TextViewerModal: FC = () => {
  const { textViewerState, closeTextViewer } = useAttachmentContext();
  const modalRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      closeTextViewer();
      setIsClosing(false);
    }, 150);
  }, [closeTextViewer]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside as unknown as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as unknown as EventListener);
    };
  }, [handleClose]);

  const mainContentDisplay = useMemo(() => {
    const file = textViewerState.file;
    if (!file) return null;

    if (file.type === 'code' && file.content) {
      return <CodeDisplay
        content={cleanCodeTags(file.content)}
        language={file.codeLanguage || 'text'}
        showHeader={false}
      />;
    }

    // PDFs are not read locally; render via the uploaded URL.
    if (file.type === 'pdf' && file.url) {
      return <PDFViewer content="" url={file.url} />;
    }

    // Plain text / markdown / docx — content is populated on upload.
    if (file.content) {
      return <div>{file.content}</div>;
    }

    // Binary formats (xlsx, pptx, odt, rtf, etc.) have no local preview.
    // Offer a link to open the uploaded copy in a new tab.
    if (file.url) {
      return (
        <div className="text-viewer-fallback">
          <p>Preview not available for this file type.</p>
          <a href={file.url} target="_blank" rel="noopener noreferrer">
            Open in new tab
          </a>
        </div>
      );
    }

    return <div className="text-viewer-fallback"><p>Nothing to display.</p></div>;
  }, [textViewerState.file]);

  if (!textViewerState.isOpen && !isClosing) return null;
  
  return (
    <div className={`text-viewer-modal ${isClosing ? 'modal-closing' : ''}`}>
      <div className="modal-content" ref={modalRef}>
        <div className="modal-header">
          <h1>{textViewerState.file?.name}</h1>
          
          <CloseButton onClick={handleClose} />
        </div>
        <div className="content-display">{mainContentDisplay}</div>
      </div>
    </div>
  )
}